import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import {
  TRAVEL_FREQUENCY_OPTIONS,
  FREQUENCY_OPTIONS,
  MBI_FREQUENCY_OPTIONS,
} from 'constants/options';
import {
  filterMap,
  getProviderOptionsWithImg,
  toDropdownOptions,
} from 'helpers';
import { useInsuranceProviders } from 'hooks/insuranceProviders';
import { usePolicySubTypes } from 'hooks/usePolicySubTypes';
import { selectFieldOptions } from 'selectors';
import { selectReferenceState } from 'selectors/referenceSelectors';
import { IPolicy } from 'types';

const usePolicyOptions = (policy: IPolicy) => {
  const { policyProviderId, policyType, policyTypeId } = policy;
  const referenceData = useSelector(selectReferenceState);
  const fieldOptions = useSelector(selectFieldOptions(policyType));

  const {
    insuranceProviders,
    loading: providerLoading,
  } = useInsuranceProviders(policyTypeId);

  const { subTypes, loading: subTypesLoading } = usePolicySubTypes(
    policyProviderId,
    policyType,
  );

  const frequencyOptions =
    policyType === INSURANCE_TYPES_NAMES.TRAVEL
      ? TRAVEL_FREQUENCY_OPTIONS
      : policyType === INSURANCE_TYPES_NAMES.MBI
        ? MBI_FREQUENCY_OPTIONS
        : FREQUENCY_OPTIONS;

  const options = useMemo(
    () => ({
      frequency: frequencyOptions,
      policyProviderId: getProviderOptionsWithImg(
        insuranceProviders,
        referenceData.policyProviders,
        policy,
      ),
      policySubTypeId: subTypes,
      policyType: toDropdownOptions(
        filterMap(R.prop('life'), R.prop('name'), referenceData.policyTypes),
      ),
      ...fieldOptions,
    }),
    [
      fieldOptions,
      frequencyOptions,
      insuranceProviders,
      policy,
      referenceData.policyProviders,
      referenceData.policyTypes,
      subTypes,
    ],
  );

  return {
    options,
    loading: {
      policySubTypeId: subTypesLoading,
      policyProviderId: providerLoading,
    },
  };
};

export default usePolicyOptions;
